import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AccessLogFilters, AccessLogResponse, UpdateProfileData } from './user-access-log.types';
import { User } from './user.types';
import { UserService } from './user.service';

/** localStorage key for preferred language (avoids API call on each reload) */
export const PREFERRED_LANGUAGE_KEY = 'preferred_language';

/** Backend codes (ESP/ENG/TUR/POR). Transloco uses es/en/tr/pt. */
const TRANSLOCO_TO_BACKEND: Record<string, string> = {
    es: 'ESP',
    en: 'ENG',
    tr: 'TUR',
    pt: 'POR',
};
const BACKEND_TO_TRANSLOCO: Record<string, string> = {
    ESP: 'es',
    ENG: 'en',
    TUR: 'tr',
    POR: 'pt',
};

@Injectable({ providedIn: 'root' })
export class UserProfileService {
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);

    /**
     * Update user profile
     * Actualiza el perfil del usuario (nombre, email, contraseña, idioma preferido)
     *
     * @param userData - Datos del usuario a actualizar
     * @returns Observable con el usuario actualizado
     */
    updateProfile(userData: UpdateProfileData): Observable<User> {
        return this._httpClient.put<User>('api/profile', userData);
    }

    /**
     * Update preferred language and persist to localStorage.
     * PUT api/profile/language — backend: ESP, ENG, TUR, POR.
     * @param lang - Transloco lang id ("es" | "en" | "tr" | "pt")
     */
    updatePreferredLanguage(lang: string): Observable<{ message: string; data: { preferred_language: string } }> {
        const backendLang = TRANSLOCO_TO_BACKEND[lang] ?? lang;
        return this._httpClient.put<{ message: string; data: { preferred_language: string } }>(
            'api/profile/language',
            { preferred_language: backendLang },
        ).pipe(
            tap((response) => {
                const updatedLang = response.data?.preferred_language ?? backendLang;
                const translocoLang = BACKEND_TO_TRANSLOCO[updatedLang] ?? lang;
                localStorage.setItem(PREFERRED_LANGUAGE_KEY, translocoLang);
            }),
        );
    }

    /**
     * Get preferred language from localStorage (no API call).
     */
    getPreferredLanguageFromStorage(): string | null {
        return localStorage.getItem(PREFERRED_LANGUAGE_KEY);
    }

    /**
     * Set preferred language in localStorage only (e.g. after login when user has it).
     * @param lang - Backend format (ESP, ENG, TUR, POR) or Transloco (es, en, tr, pt)
     */
    setPreferredLanguageInStorage(lang: string): void {
        const translocoLang = BACKEND_TO_TRANSLOCO[lang] ?? lang;
        localStorage.setItem(PREFERRED_LANGUAGE_KEY, translocoLang);
    }

    /** Convert backend language (ESP, ENG, TUR, POR) to Transloco (es, en, tr, pt). */
    toTranslocoLang(backendLang: string | null | undefined): string | null {
        if (!backendLang) return null;
        return BACKEND_TO_TRANSLOCO[backendLang] ?? null;
    }

    /**
     * Get user access logs
     * Obtiene el historial de accesos del usuario actual con filtros y paginación
     * 
     * @param filters - Filtros opcionales para la consulta
     * @returns Observable con los logs de acceso paginados
     */
    getUserAccessLogs(filters?: AccessLogFilters): Observable<AccessLogResponse> {
        let params = new HttpParams();

        if (filters) {
            // Parámetros según documentación API
            if (filters.action) {
                params = params.set('action', filters.action);
            }
            if (filters.result) {
                params = params.set('result', filters.result);
            }
            if (filters.from) {
                params = params.set('from', filters.from);
            }
            if (filters.to) {
                params = params.set('to', filters.to);
            }
            if (filters.order_by) {
                params = params.set('order_by', filters.order_by);
            }
            if (filters.order_direction) {
                params = params.set('order_direction', filters.order_direction);
            }
            if (filters.per_page) {
                params = params.set('per_page', filters.per_page.toString());
            }
            if (filters.page) {
                params = params.set('page', filters.page.toString());
            }
            
            // Mantener compatibilidad con parámetros antiguos
            if (filters.startDate && !filters.from) {
                params = params.set('from', filters.startDate);
            }
            if (filters.endDate && !filters.to) {
                params = params.set('to', filters.endDate);
            }
            if (filters.eventType && !filters.action) {
                params = params.set('action', filters.eventType);
            }
            if (filters.success !== undefined && !filters.result) {
                params = params.set('result', filters.success ? 'success' : 'failed');
            }
            if (filters.limit && !filters.per_page) {
                params = params.set('per_page', filters.limit.toString());
            }
        }

        return this._httpClient.get<AccessLogResponse>('api/profile/access-logs', { params });
    }

    /**
     * Export user access logs to CSV or JSON
     * Exporta los logs de acceso en formato CSV (compatible Excel) o JSON
     * 
     * @param filters - Filtros opcionales para la exportación
     * @param format - Formato de exportación: 'csv' (default) o 'json'
     * @returns Observable con el blob del archivo
     */
    exportAccessLogs(filters?: AccessLogFilters, format: 'csv' | 'json' = 'csv'): Observable<Blob> {
        let params = new HttpParams();
        
        // Establecer formato
        params = params.set('format', format);

        if (filters) {
            // Nuevos parámetros del backend
            if (filters.action) {
                params = params.set('action', filters.action);
            }
            if (filters.result) {
                params = params.set('result', filters.result);
            }
            if (filters.from) {
                params = params.set('from', filters.from);
            }
            if (filters.to) {
                params = params.set('to', filters.to);
            }
            
            // Mantener compatibilidad con parámetros antiguos
            if (filters.startDate) {
                params = params.set('from', filters.startDate);
            }
            if (filters.endDate) {
                params = params.set('to', filters.endDate);
            }
            if (filters.eventType) {
                params = params.set('action', filters.eventType);
            }
        }

        return this._httpClient.get('api/profile/access-logs/export', {
            params,
            responseType: 'blob'
        });
    }
}
